// M+3.6 C5 — single-source-of-truth lookups that replace five hardcoded
// constant literals previously scattered across the codebase. Each helper
// takes the raw TaxonomyEntry[] from useTaxonomyList(kind) (the API hook
// returns the entries verbatim, including .meta) and returns the minimal
// shape each call site cares about.
//
// Defaults preserve the legacy visuals when the taxonomy isn't loaded yet
// (initial mount, API error fallback) — matching the rgba/var pairs seeded
// by the C4 migration so first paint and hydrated paint look identical.
//
// Phase M+3.7 — labels resolve via pickEntryLabel which reads the active
// user language from window.__lxLang and falls back labelXX → labelEn →
// label → code. Defaults below stay English (the safe-fallback layer).
import type { TaxEntry, TaxList, TaxTuple, TaxValue } from "./taxonomyTypes";
import {
  pickEntryLabel,
  pickEntryLabelIn,
  resolveLongLabel as _resolveLongLabel,
} from "../utils/taxonomyMap";
// 05n — THE MATCH ITSELF LIVES IN ONE FILE, and this is the second half of the
// argument step C made for merging `ctc` into `resolveTaxonomyEntry`. The census
// and the migration ask the same question this module asks, from Node, about
// rows they are going to REWRITE. A copy of the five-facet filter over there
// would be step C's defect again with a data migration's blast radius.
import { claimants, valueState as _valueState } from "./taxonomyMatch.mjs";

// Phase 7 Stage 2 — convenience re-export so callers that already import
// from taxonomyHelpers can pick up the long-label resolver without adding
// a second import line. Behaves identically to the source: returns the
// active-lang long label from entry.meta with a cascade to the short
// pickEntryLabel.
export const resolveLongLabel = _resolveLongLabel;

const TASK_STATUS_DEFAULTS: Record<string, { label: string; color: string; bgColor: string }> = {
  todo: { label: "To do", color: "var(--ts)", bgColor: "rgba(80,90,110,.22)" },
  inprogress: {
    // Round 13 (C78) — "In progress" ate row space; the canonical EN value
    // is "Ongoing" now (FR "En cours", NL "Bezig" — the DB taxonomy rows are
    // updated by seed_migration_phase29.js; this is the offline fallback).
    label: "Ongoing",
    color: "var(--am)",
    bgColor: "var(--ams)",
  },
  waiting: { label: "Waiting", color: "var(--rd)", bgColor: "var(--rs)" },
  completed: { label: "Completed", color: "var(--gn)", bgColor: "var(--gs)" },
  cancelled: {
    label: "Cancelled",
    color: "var(--tm)",
    bgColor: "rgba(80,90,110,.15)",
  },
  archived: {
    label: "Archived",
    color: "var(--tm)",
    bgColor: "rgba(80,90,110,.15)",
  },
};

const INVOICE_STATUS_DEFAULTS: Record<string, { label: string; color: string; bgColor: string }> = {
  draft: { label: "Draft", color: "var(--tm)", bgColor: "rgba(80,90,110,.15)" },
  ready: { label: "Ready", color: "var(--pp)", bgColor: "var(--ps)" },
  unpaid: { label: "Unpaid", color: "var(--am)", bgColor: "var(--ams)" },
  partially_paid: {
    label: "Partial",
    color: "var(--am)",
    bgColor: "var(--ams)",
  },
  paid: { label: "Paid", color: "var(--gn)", bgColor: "var(--gs)" },
  cancelled: { label: "Cancelled", color: "var(--rd)", bgColor: "var(--rs)" },
};

const WORK_LOCATION_DEFAULTS: Record<string, { label: string; color: string }> = {
  onsite: { label: "Onsite", color: "var(--ac)" },
  thirdparty: { label: "Third Party", color: "var(--am)" },
  home: { label: "Home", color: "var(--gn)" },
  /* 157A §10(B) RULED (2026-08-31) — "Not working" is a PLAIN RED FLAT FILL
     and it lives HERE, beside the other three, so `_LOC_CLR` answers for it
     the same way it answers for them. It is deliberately NOT a member of
     `workLocationCycle` (which returns the three working locations, or the
     tenant's taxonomy): this is a paint mode that writes an explicit null,
     not a place anyone works. Nothing enumerates this object — the only
     reader is getWorkLocationMeta's `def` lookup below — so adding a fourth
     key cannot leak a fourth option into a picker. */
  notworking: { label: "Not working", color: "var(--rd)" },
};

// Phase 7 round 4 hardening — Basis Doc §4.1 invariant: "Helpers that
// consume display-list output must accept both shapes — do not assume
// `.code` on the iteration variable." useTaxonomyList(kind) returns the
// legacy display shape per kind: tuple `[code, label]` for the `tuple`
// family (task_status, reminder_option, …), raw TaxonomyEntry rows for
// most others. Call sites have repeatedly passed tuples to helpers that
// expected raw entries — silent fallback to hardcoded English defaults
// is the symptom. lookup() now branches on first-element shape and
// reconstitutes a minimal entry-like row from a tuple so downstream
// pickEntryLabel / meta access stays consistent. The tuple path loses
// meta (color/bgColor/icon) — callers fall back to the per-kind
// DEFAULTS for those fields, which is the safest degradation when the
// raw entries weren't provided. Pass raw entries
// (useTaxonomy(kind).entries) when you need meta access; tuples are
// fine when only label resolution matters.
function lookup(entries: any, code: any): any {
  if (!entries || !Array.isArray(entries) || entries.length === 0) return null;
  const first = entries.find((e) => e != null);
  if (first == null) return null;
  if (Array.isArray(first)) {
    // Tuple shape [code, label]. Reconstitute a minimal entry-like
    // object so pickEntryLabel returns the active-lang label that
    // toDisplayList already resolved.
    const t = entries.find((x) => Array.isArray(x) && x[0] === code);
    if (!t) return null;
    return { code: t[0], label: t[1], labelEn: t[1], _fromTuple: true };
  }
  return entries.find((e) => e && e.code === code) || null;
}

// Note on the [partially_paid | "partially paid"] divergence: the legacy
// ST_CFG literal supported both spellings. The taxonomy canon is
// "partially_paid" (snake_case). This helper normalises by stripping the
// space form to the underscore form before lookup.
function normalizeInvoiceCode(code: any) {
  if (typeof code !== "string") return code;
  return code === "partially paid" ? "partially_paid" : code;
}

export function getTaskStatusMeta(code: any, entries: any) {
  const e = lookup(entries, code);
  const def = TASK_STATUS_DEFAULTS[code] || TASK_STATUS_DEFAULTS.todo;
  if (!e) return def;
  const meta =
    e.meta && typeof e.meta === "object" && !Array.isArray(e.meta)
      ? e.meta
      : {};
  return {
    label: pickEntryLabel(e) || def.label,
    color: meta.color || def.color,
    bgColor: meta.bgColor || def.bgColor,
  };
}

/* BRIEF 136 §3 — the four event statuses, as the offline/first-paint fallback.
   `icon` is the Lucide name of the glyph that REPLACES the day counter, and the
   two that declare none are the two that draw it: `confirmed`, which is what a
   NULL column resolves to, and `archived`, which is the Phase-28 boolean's word
   rather than a value of the column (src/lib/eventStatus.ts).
   The colours are `task_status`'s own for the codes that mean the same thing —
   an unconfirmed event is amber like an ongoing task, a cancelled one is muted
   like a cancelled task — so the two vocabularies read alike on one screen. */
const EVENT_STATUS_DEFAULTS: Record<
  string,
  { label: string; color: string; bgColor: string; icon: string }
> = {
  confirmed: {
    label: "Confirmed",
    color: "var(--gn)",
    bgColor: "var(--gs)",
    icon: "",
  },
  unconfirmed: {
    label: "Unconfirmed",
    color: "var(--am)",
    bgColor: "var(--ams)",
    icon: "CircleQuestionMark",
  },
  cancelled: {
    label: "Cancelled",
    color: "var(--tm)",
    bgColor: "rgba(80,90,110,.15)",
    icon: "CircleOff",
  },
  archived: {
    label: "Archived",
    color: "var(--tm)",
    bgColor: "rgba(80,90,110,.15)",
    icon: "",
  },
};

/** 136 §3 — label + colours + the counter-replacing glyph for one event status.
 *  `icon: ""` is a real answer and means "draw the counter": an admin who
 *  clears the cell in Settings gets the ordinary countdown, never a fallback
 *  glyph. (That distinction was drawn against `getChatTypeMeta`, which fell
 *  through to a wrong glyph; 137 §5 retired it along with its taxonomy.) */
export function getEventStatusMeta(
  code: string,
  entries?: unknown,
): { label: string; color: string; bgColor: string; icon: string } {
  /* TYPED, where its four neighbours in this file take `(code: any, entries:
     any)`. `scripts/lint/ratchets.js` refused the copied signature and it was
     right to: the house shape here is history, and new code does not inherit
     it (SESSION_RULES — a ratchet is not re-recorded to make it pass). */
  const e = lookup(entries, code);
  const def = EVENT_STATUS_DEFAULTS[code] || EVENT_STATUS_DEFAULTS.confirmed;
  if (!e) return def;
  const meta: { color?: string; bgColor?: string; icon?: string } =
    e.meta && typeof e.meta === "object" && !Array.isArray(e.meta)
      ? e.meta
      : {};
  return {
    label: pickEntryLabel(e) || def.label,
    color: meta.color || def.color,
    bgColor: meta.bgColor || def.bgColor,
    /* NOT `meta.icon || def.icon`: an admin who empties the cell means the
       counter, and `||` would read that as "unset" and hand back the seeded
       glyph — a control that cannot be turned off. A string, however empty, is
       the row's answer. */
    icon: typeof meta.icon === "string" ? meta.icon : def.icon,
  };
}

export function getInvoicePaymentStatusMeta(code: any, entries: any) {
  const norm = normalizeInvoiceCode(code);
  const e = lookup(entries, norm);
  const def = INVOICE_STATUS_DEFAULTS[norm] || INVOICE_STATUS_DEFAULTS.draft;
  if (!e) return def;
  const meta =
    e.meta && typeof e.meta === "object" && !Array.isArray(e.meta)
      ? e.meta
      : {};
  return {
    label: pickEntryLabel(e) || def.label,
    color: meta.color || def.color,
    bgColor: meta.bgColor || def.bgColor,
  };
}


export function getWorkLocationMeta(code: any, entries: any) {
  const e = lookup(entries, code);
  const def = WORK_LOCATION_DEFAULTS[code] || WORK_LOCATION_DEFAULTS.onsite;
  if (!e) return def;
  const meta =
    e.meta && typeof e.meta === "object" && !Array.isArray(e.meta)
      ? e.meta
      : {};
  return {
    label: pickEntryLabel(e) || def.label,
    color: meta.color || def.color,
  };
}

// 05m step C — THE ONE MATCHER. Which entry does a stored value name?
//
// It existed twice, to different depths, and that asymmetry WAS a live defect:
// `resolveTaxonomyLabel` below matched all five facets while `ctc` (utils.ts)
// matched only code/labelEn/label, so a case type chosen in French or Dutch
// resolved for its NAME and not for its COLOUR. One matcher, one answer.
//
// WHY IT STILL MATCHES LABELS AFTER 05n. Six kinds used to store the LABEL, in
// whatever language the user had loaded at save time — prod's census
// (2026-08-03) has `CollectiveCase.type` holding both `Financial` and
// `Financier`, one entry, two strings. 05n converts those columns to codes, and
// the five-facet match does NOT become redundant when it does: brief 23 §3
// leaves a residue unconverted on purpose, the importers resolve free-text
// spreadsheet cells through this same function, and a row written before the
// migration by a client that had not reloaded is the same shape. A matcher that
// only knew codes would call every one of those an orphan.
//
// Returns the ENTRY, not a label, because callers want different things from
// it — the label, `meta.color`, or merely whether it resolved at all (the
// orphan signal). Null means the vocabulary does not contain this value.
export function resolveTaxonomyEntry(value: TaxValue, entries: TaxList): TaxEntry | null {
  if (value == null || value === "") return null;
  if (!entries || !Array.isArray(entries) || entries.length === 0) return null;
  // Shape-tolerant per Basis §4.1 (see lookup() comment above). Tuple form
  // `[code, label]` carries only the label toDisplayList already picked for
  // the active language; reconstitute the minimal entry so callers get one
  // shape back. A tuple has no `meta`, so a meta-reading caller correctly
  // gets nothing rather than a wrong colour.
  const first = entries.find((e) => e != null);
  if (first != null && Array.isArray(first)) {
    const t = entries.find((x) => Array.isArray(x) && x[0] === value);
    const tup = t as TaxTuple | undefined;
    return tup ? { code: tup[0], label: tup[1], labelEn: tup[1], _fromTuple: true } : null;
  }
  const hits = claimants(value, entries as TaxEntry[]) as TaxEntry[];
  return hits.length === 1 ? hits[0] : null;
}

// What does this column value actually say?
//
//   ok          exactly one entry claims it
//   ambiguous   two or more DIFFERENT entries claim it — see above
//   unresolved  the vocabulary does not contain it (the renamed-entry orphan)
//   unknown     nothing to say: no value, or the kind has not loaded
export type TaxState = "ok" | "ambiguous" | "unresolved" | "unknown";
export function taxonomyValueState(value: TaxValue, entries: TaxList): TaxState {
  if (value == null || value === "") return "unknown";
  if (!entries || !Array.isArray(entries) || entries.length === 0) return "unknown";
  const first = entries.find((e) => e != null);
  if (first != null && Array.isArray(first)) {
    return entries.some((x) => Array.isArray(x) && x[0] === value) ? "ok" : "unresolved";
  }
  return _valueState(value, entries as TaxEntry[]) as TaxState;
}

// 05m step C §5 — does this column hold something the vocabulary does not
// contain? The failure this slice exists to prevent, and prod has one:
// `Case.stages[].type = "Memo, legal opinion"`, an entry renamed after a case
// was saved (census, 2026-08-03).
//
// AN UNLOADED KIND IS NOT AN ORPHAN, and that is the whole care in this
// function. `entries` is empty during hydration and after a failed fetch, and
// treating either as "no entry matches" would paint a re-pick warning across
// every row of a healthy database for one frame — the instrument crying wolf
// is how it stops being read. Absent evidence, this answers false.
export function isTaxonomyOrphan(value: TaxValue, entries: TaxList): boolean {
  return taxonomyValueState(value, entries) === "unresolved";
}

// Phase M+3.7 Stage 3 round 3 — resolve a stored value (code OR English label)
// against a TaxonomyEntry list to its active-language label. Used by views
// that render `cs.status` / `cs.ctype` directly (CaseView header, ReportsView
// table, CollectiveCaseView, CoView in App.jsx). Falls back to the input
// value when no entry matches, keeping unknown codes visible instead of
// silently swallowing them.
// 206E §4.3 — WIDENED (rule 4): `lang` resolves the label in another language
// than the reader's; absent, exactly what it was.
export function resolveTaxonomyLabel(value: any, entries: any, lang?: string) {
  if (value == null || value === "") return value;
  const e = resolveTaxonomyEntry(value, entries);
  return e ? pickEntryLabelIn(e, lang) : value;
}

// Phase 7 — resolve a subtask-template entry's label by active language.
// Template shape: {labelEn, labelFr, labelNl, offsetDays}. Legacy entries
// may still carry a single `label` field (pre-migration). Falls back
// labelXX → labelEn → label → "". Reads window.__lxLang the same way
// pickEntryLabel does.
export function resolveTemplateLabel(tpl: any) {
  if (!tpl || typeof tpl !== "object") return "";
  const lang = (typeof window !== "undefined" && window.__lxLang) || "en";
  if (lang === "fr" && tpl.labelFr) return tpl.labelFr;
  if (lang === "nl" && tpl.labelNl) return tpl.labelNl;
  return tpl.labelEn || tpl.label || "";
}

// Cycle order for paint-mode + click-to-cycle in Schedule views. Falls
// back to the legacy onsite → thirdparty → home order when the taxonomy
// isn't loaded yet.
//
// Accepts either raw TaxonomyEntry rows ({code, archived, ...}) or the
// tuple-shaped output from useTaxonomyList (`[code, label]` pairs). The
// tuple variant is what the Schedule views actually pass — work_location
// is in the `tuple` shape family in taxonomyMap.js, so useTaxonomyList
// returns [['onsite','Onsite'], ['thirdparty','Third Party'], …].
// Without this dual handling the `.code` access on a tuple returns
// undefined, and downstream callers render "teamSchedule.undefined".
export function workLocationCycle(entries: any) {
  if (!entries || !Array.isArray(entries) || entries.length === 0) {
    return ["onsite", "thirdparty", "home"];
  }
  return entries
    .map((e) => (Array.isArray(e) ? e[0] : e && !e.archived ? e.code : null))
    .filter(Boolean);
}

// Status-tag dropdown options for the task status picker. Returns the
// active (non-archived) statuses in display order; the picker filters
// downstream as needed.
export function getTaskStatusOptions(entries: any) {
  if (!entries || !Array.isArray(entries) || entries.length === 0) {
    return Object.entries(TASK_STATUS_DEFAULTS).map(([code, def]) => ({
      code,
      ...def,
    }));
  }
  return entries
    .filter((e) => e && !e.archived)
    .map((e) => ({
      code: e.code,
      ...getTaskStatusMeta(e.code, entries),
    }));
}
